import {computed} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useAuth} from '#imports';
import {useProfileSuggestionsStore} from '~/features/profile-suggestions/store/profile-suggestions.store';
import {useFeatureContextStore} from '~/store/featureStore';
import {useSimilarProfileStore} from '~/store/similars/similarProfiles.store';
import {SectionsVisibilityType, sectionsVisibilityTypesFromBackend} from '~/types/domain/SectionsVisibility';
import type {SectionsVisibilityTypeFromBackend} from '~/types/domain/SectionsVisibility';
import {useProfileStore} from './profileStore';
import {useReputationStore} from './reputation/reputationStore';

export const useSectionsVisibilityStore = defineStore('sectionsVisibility', () => {
    const profileStore = useProfileStore();

    const {features} = storeToRefs(useFeatureContextStore());
    const {sectionsVisibility} = storeToRefs(useProfileStore());
    const sectionVisibilityMapAccordingToBackend = computed<Record<SectionsVisibilityTypeFromBackend, boolean>>(() => {
        return Object.values(sectionsVisibilityTypesFromBackend).reduce(
            (acc, section) => {
                acc[section] = sectionsVisibility.value?.visibleSections?.includes(section) ?? false;
                return acc;
            },
            {} as Record<SectionsVisibilityTypeFromBackend, boolean>,
        );
    });

    const {hasSuggestions} = storeToRefs(useProfileSuggestionsStore());
    const {similarProfiles} = storeToRefs(useSimilarProfileStore());
    const {appraisals, industryExpertises, portfolio, profile, experiences, educations, certifications, relations} = storeToRefs(profileStore);
    const isEditionEnabled = computed(() => profile.value?.editMode || false);
    const {loggedIn} = storeToRefs(useAuth());
    const {reputations} = storeToRefs(useReputationStore());

    const hasToDisplayIndustryExpertises = computed<boolean>(() => isEditionEnabled.value || (industryExpertises.value?.length ?? 0) > 0);
    const hasPortfolioItems = computed<boolean>(() => (portfolio.value?.items.length ?? 0) > 0);
    const hasToDisplayPortfolio = computed<boolean>(() => isEditionEnabled.value || hasPortfolioItems.value);
    const hasToDisplayDescription = computed<boolean>(() => isEditionEnabled.value || (profile.value?.description?.length ?? 0) > 0);
    const hasToDisplayExperiences = computed<boolean>(() => isEditionEnabled.value || (experiences.value?.items.length ?? 0) > 0);
    const hasToDisplayEducations = computed<boolean>(() => isEditionEnabled.value || (educations.value?.items.length ?? 0) > 0);
    const hasToDisplayCertifications = computed<boolean>(() => isEditionEnabled.value || (certifications.value?.items.length ?? 0) > 0);
    const hasToDisplayAppraisals = computed<boolean>(() => isEditionEnabled.value || (appraisals.value?.appraisals.length ?? 0) > 0);
    const hasToDisplayLanguages = computed<boolean>(() => isEditionEnabled.value || (profile.value?.languages?.items?.length ?? 0) > 0);
    const hasToDisplayProfileRelations = computed<boolean>(() => !!relations.value);
    const hasSimilarProfiles = computed(() => similarProfiles.value && similarProfiles.value.length > 0);
    const blurSimilarProfiles = computed(() => !loggedIn.value);
    const hasToDisplaySimilarProfiles = computed<boolean>(() => !isEditionEnabled.value && (hasSimilarProfiles.value || blurSimilarProfiles.value));
    const hasToDisplaySuggestedForYou = computed<boolean>(
        () => !!features.value['profile-suggestion'] && isEditionEnabled.value && hasSuggestions.value,
    );
    const hasToDisplayAlertBlock = computed<boolean>(() => (profile.value && profile.value!.editMode) ?? false);
    const hasToDisplayReputation = computed<boolean>(() => isEditionEnabled.value || (reputations.value.length ?? 0) > 0);

    const sectionVisibility = computed<Record<SectionsVisibilityType, boolean>>(() => ({
        [SectionsVisibilityType.TASK_LIST]: isEditionEnabled.value,
        [SectionsVisibilityType.SUGGESTED_FOR_YOU]: hasToDisplaySuggestedForYou.value,
        [SectionsVisibilityType.DESCRIPTION]: hasToDisplayDescription.value,
        [SectionsVisibilityType.EXPERIENCES]: hasToDisplayExperiences.value,
        [SectionsVisibilityType.EDUCATIONS]: hasToDisplayEducations.value,
        [SectionsVisibilityType.CERTIFICATIONS]: hasToDisplayCertifications.value,
        [SectionsVisibilityType.APPRAISALS]: hasToDisplayAppraisals.value,
        [SectionsVisibilityType.PROFILE_RELATIONS]: hasToDisplayProfileRelations.value,
        [SectionsVisibilityType.LANGUAGES]: hasToDisplayLanguages.value,
        [SectionsVisibilityType.VISIBILITY_ALERT_BLOCK]:
            sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.VISIBILITY_ALERT_BLOCK] && hasToDisplayAlertBlock.value,
        [SectionsVisibilityType.PORTFOLIO]:
            hasToDisplayPortfolio.value && sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.PORTFOLIO],
        [SectionsVisibilityType.EXTERNAL_REPUTATION]:
            hasToDisplayReputation.value && sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.EXTERNAL_REPUTATION],
        [SectionsVisibilityType.SHARE_YOUR_PROFILE]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.SHARE_YOUR_PROFILE],
        [SectionsVisibilityType.SIMILAR_PROFILES]:
            sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.SIMILAR_PROFILES] && hasToDisplaySimilarProfiles.value,
        [SectionsVisibilityType.PROPOSE_A_PROJECT]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.PROPOSE_A_PROJECT],
        [SectionsVisibilityType.ADD_TO_FAVORITES]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.ADD_TO_FAVORITES],
        [SectionsVisibilityType.FUNCTIONAL_SKILLS]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.FUNCTIONAL_SKILLS],
        [SectionsVisibilityType.INDUSTRY_EXPERTISE]:
            hasToDisplayIndustryExpertises.value && sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.INDUSTRY_EXPERTISE],
        [SectionsVisibilityType.WORKPLACE_PREFERENCES]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.WORKPLACE_PREFERENCES],
        [SectionsVisibilityType.ONBOARDING_STRATEGY]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.ONBOARDING_STRATEGY],
        [SectionsVisibilityType.RECOMMENDATIONS]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.RECOMMENDATIONS],
        [SectionsVisibilityType.BREADCRUMBS]:
            !isEditionEnabled.value && sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.BREADCRUMBS],
        [SectionsVisibilityType.BADGES]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.BADGES],
        [SectionsVisibilityType.TRANSLATION]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.TRANSLATION],
        [SectionsVisibilityType.ASYNC_TRANSLATION]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.ASYNC_TRANSLATION],
        [SectionsVisibilityType.SEARCH_LINKS]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.SEARCH_LINKS],
        [SectionsVisibilityType.VIEW_MORE_LOCK]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.VIEW_MORE_LOCK],
        [SectionsVisibilityType.PROFILE_PRODUCT_TOUR]: sectionVisibilityMapAccordingToBackend.value[SectionsVisibilityType.PROFILE_PRODUCT_TOUR],
    }));

    return {
        sectionVisibility,

        hasSimilarProfiles,
        blurSimilarProfiles,
        hasPortfolioItems,
    };
});
